import React, { useState } from "react";
import { Spinner, Modal } from "react-bootstrap";
import axios from "axios";
import { toast } from "react-toastify";
import { useParams } from "react-router-dom";
import { useAuth } from "../Context/AuthContext";
import { Base_URL } from "../url";

/* ─────────────────────────────────────────────────────────────────
   Design tokens — single source of truth
───────────────────────────────────────────────────────────────── */
const T = {
  indigo:      "#4f46e5",
  indigoLight: "#eef2ff",
  indigoMid:   "#c7d2fe",
  green:       "#16a34a",
  greenLight:  "#f0fdf4",
  gray50:      "#f5f6fa",
  gray100:     "#f0f1f5",
  gray200:     "#e9eaf0",
  gray300:     "#d1d5db",
  gray400:     "#9ca3af",
  gray600:     "#6b7280",
  gray800:     "#374151",
  gray900:     "#111827",
  white:       "#ffffff",
  red50:       "#fef2f2",
  red200:      "#fecaca",
  red800:      "#991b1b",
  blue50:      "#f0f9ff",
  blue700:     "#0369a1",
  radius:      12,
  radiusSm:    8,
  radiusLg:    16,
  shadow:      "0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)",
};

/* ─────────────────────────────────────────────────────────────────
   Tiny primitive components
───────────────────────────────────────────────────────────────── */

/** Dismissible alert banner */
function Banner({ type, msg, onClose }) {
  if (!msg) return null;
  const isDanger = type === "danger";
  return (
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
      background: isDanger ? T.red50 : T.greenLight,
      border: `1px solid ${isDanger ? T.red200 : "#bbf7d0"}`,
      borderRadius: T.radius, color: isDanger ? T.red800 : "#14532d",
      padding: "11px 16px", fontSize: 13, marginBottom: 16,
    }}>
      <span>{msg}</span>
      {onClose && (
        <button onClick={onClose} style={{
          background: "none", border: "none", cursor: "pointer",
          fontSize: 15, color: "inherit", opacity: 0.5, padding: 0, lineHeight: 1,
        }} aria-label="Dismiss">✕</button>
      )}
    </div>
  );
}

/** Unified button */
function Btn({ variant = "primary", size = "md", onClick, disabled, children, type = "button" }) {
  const sm = size === "sm";
  const variants = {
    primary: { background: T.indigo,   color: T.white, border: "none" },
    success: { background: T.green,    color: T.white, border: "none" },
    outline: { background: T.white,    color: T.indigo, border: `1px solid ${T.indigoMid}` },
    ghost:   { background: T.gray100,  color: T.gray800, border: `1px solid ${T.gray200}` },
    danger:  { background: "#dc2626",  color: T.white, border: "none" },
  };
  return (
    <button
      type={type}
      disabled={disabled}
      onClick={!disabled ? onClick : undefined}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6, cursor: disabled ? "not-allowed" : "pointer",
        borderRadius: sm ? T.radiusSm : T.radius, fontWeight: 600, whiteSpace: "nowrap",
        fontSize: sm ? 12 : 13, padding: sm ? "7px 13px" : "9px 18px",
        opacity: disabled ? 0.45 : 1, transition: "opacity 0.15s",
        fontFamily: "inherit",
        ...(variants[variant] || variants.primary),
      }}
    >
      {children}
    </button>
  );
}

/** Labelled form field */
function Field({ label, children }) {
  return (
    <div>
      {label && <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: T.gray800, marginBottom: 6, letterSpacing: "0.15px" }}>{label}</label>}
      {children}
    </div>
  );
}

/** Styled text/date/time/number input */
function Input({ value, onChange, type = "text", placeholder, readOnly, style, step }) {
  return (
    <input
      type={type} value={value} onChange={onChange} placeholder={placeholder}
      readOnly={readOnly} step={step}
      style={{
        width: "100%", height: 40, borderRadius: T.radius, border: `1px solid ${T.gray200}`,
        padding: "0 12px", fontSize: 14, color: T.gray900, background: readOnly ? T.gray50 : T.white,
        outline: "none", boxSizing: "border-box", fontFamily: "inherit",
        ...(style || {}),
      }}
    />
  );
}

/** Compact table input */
function CellInput({ value, onChange, type = "text", placeholder, step, style }) {
  return (
    <input
      type={type} value={value} onChange={onChange} placeholder={placeholder} step={step}
      style={{
        height: 34, borderRadius: T.radiusSm, border: `1px solid ${T.gray200}`,
        padding: "0 10px", fontSize: 13, color: T.gray900, background: T.white,
        outline: "none", boxSizing: "border-box", fontFamily: "inherit", width: "100%",
        ...(style || {}),
      }}
    />
  );
}

/** Section divider label */
function SectionLabel({ children }) {
  return (
    <div style={{
      fontSize: 11, fontWeight: 700, color: T.gray400, textTransform: "uppercase",
      letterSpacing: "0.7px", margin: "18px 0 10px",
    }}>
      {children}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
   Card wrapper
───────────────────────────────────────────────────────────────── */
function Card({ title, children, style }) {
  return (
    <div style={{
      background: T.white, borderRadius: T.radiusLg, border: `1px solid ${T.gray200}`,
      padding: "20px 24px", marginBottom: 20, boxShadow: T.shadow, ...(style || {}),
    }}>
      {title && (
        <div style={{
          fontSize: 12, fontWeight: 700, color: T.gray600, textTransform: "uppercase",
          letterSpacing: "0.6px", marginBottom: 16, paddingBottom: 12,
          borderBottom: `1px solid ${T.gray100}`,
        }}>
          {title}
        </div>
      )}
      {children}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
   Step badge
───────────────────────────────────────────────────────────────── */
function StepBadge({ n, size = 26 }) {
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      width: size, height: size, borderRadius: "50%",
      background: T.indigoLight, color: T.indigo, fontSize: size < 24 ? 10 : 11, fontWeight: 700, flexShrink: 0,
    }}>
      {n}
    </span>
  );
}

/* ─────────────────────────────────────────────────────────────────
   Main component — ALL LOGIC UNCHANGED
───────────────────────────────────────────────────────────────── */
export default function ExecuteProductionOrder() {
  const { productionOrderId } = useParams();
  const { user } = useAuth();

  const [queryOrderId, setQueryOrderId]           = useState(productionOrderId ?? "");
  const [deliveryStatus, setDeliveryStatus]       = useState(false);
  const [confirmLoading, setConfirmLoading]       = useState(false);
  const [startDate, setStartDate]                 = useState("");
  const [startTime, setStartTime]                 = useState("");
  const [loading, setLoading]                     = useState(false);
  const [error, setError]                         = useState("");
  const [successMsg, setSuccessMsg]               = useState("");
  const [executionRows, setExecutionRows]         = useState([]);
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);
  const [deliveryData, setDeliveryData]           = useState([]);
  const [deliveryLoading, setDeliveryLoading]     = useState(false);
  const [deliveryError, setDeliveryError]         = useState("");
  const [itemMap, setItemMap]                     = useState({});
  const [actualOutputs, setActualOutputs]         = useState({});
  const [stageStarts, setStageStarts]             = useState({});
  const [deliverySfgMap, setDeliverySfgMap]       = useState({});
  const [deliveryExtras, setDeliveryExtras]       = useState({});

  /* endpoints */
  const EXECUTE_ENDPOINT        = (id) => `${Base_URL}/ProductionExecution/id?id=${encodeURIComponent(id)}`;
  const EXECUTION_FETCH         = (id) => `${Base_URL}/ProductionExecution/id?id=${encodeURIComponent(id)}`;
  const COMPLETE_STAGE_ENDPOINT = ()   => `${Base_URL}/ProductionExecution/completeStage`;

  /* validation */
  const validateBasic = (orderId) => {
    if (!startDate)    { setError("Start date is required.");          return false; }
    if (!startTime)    { setError("Start time is required.");          return false; }
    if (!orderId)      { setError("Production order id is required."); return false; }
    if (!user?.userId) { setError("User not authenticated.");          return false; }
    return true;
  };

  /* item map */
  const fetchItemMap = async () => {
    try {
      const res = await axios.get(`${Base_URL}/Item/ItemGetData`);
      const map = {};
      (Array.isArray(res?.data) ? res.data : []).forEach((item) => {
        const key  = item.itemCodeId ?? item.itemCode ?? item.item_code ?? item.itemCodeID ?? item.code;
        const name = item.itemName ?? item.itemDesc ?? item.description ?? item.name ?? "";
        if (key) map[key] = name;
      });
      setItemMap(map);
    } catch (err) { console.warn("Failed to fetch item map:", err); }
  };

  /* fetch execution rows */
  const fetchExecutionRows = async (id) => {
    if (!id) { setError("Enter a production order id and press Load Stages."); return; }
    setError(""); setSuccessMsg(""); setExecutionRows([]); setStageStarts({});
    try {
      setLoading(true);
      const res = await axios.get(EXECUTION_FETCH(id));
      const arr = Array.isArray(res?.data) ? res.data : [];
      setExecutionRows(arr);
      const init = {};
      arr.forEach((r, idx) => { init[idx] = { date: r.stageStartDate ?? "", time: r.stageStartTime ?? "", outputQty: r.stageOutputQty ?? "" }; });
      setStageStarts(init);
      if (!res || Object.keys(itemMap).length === 0) await fetchItemMap();
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message ?? err.message ?? "Failed to fetch execution rows");
    } finally { setLoading(false); }
  };

  /* start execution */
  const handleExecute = async (e) => {
    e?.preventDefault?.();
    setError(""); setSuccessMsg(""); setExecutionRows([]);
    const oid = queryOrderId || productionOrderId;
    if (!validateBasic(oid)) return;
    const sequences = executionRows.length > 0
      ? executionRows.map((r, idx) => ({ ...r, sequenceNo: r.orderProductionProcessSeq ?? r.planProductionProcessSeq ?? idx + 1 }))
      : [];
    try {
      setLoading(true);
      const res = await axios.post(EXECUTE_ENDPOINT(oid), { productionOrderId: oid, startDate, startTime, createdBy: String(user?.userId), sequences });
      setSuccessMsg("Execution started successfully.");
      await fetchExecutionRows(oid);
      console.log("Execute response:", res.data);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message ?? err.message ?? "Execution failed");
    } finally { setLoading(false); }
  };

  const updateStageStart = (rowIdx, field, value) =>
    setStageStarts((prev) => ({ ...prev, [rowIdx]: { ...(prev[rowIdx] || { date: "", time: "", outputQty: "" }), [field]: value } }));

  /* complete stage */
  const handleCompleteStage = async (row, rowIdx) => {
    setError(""); setSuccessMsg("");
    const oid = queryOrderId || productionOrderId;
    if (!oid) { setError("Missing production order id."); return; }
    const ss = stageStarts[rowIdx] ?? { date: "", time: "", outputQty: "" };
    if (!ss.date) { setError("Stage start date is required to complete the stage."); return; }
    if (!ss.time) { setError("Stage start time is required to complete the stage."); return; }
    const seqNo = row.orderProductionProcessSeq ?? row.planProductionProcessSeq ?? rowIdx + 1;
    try {
      setLoading(true);
      const res = await axios.post(COMPLETE_STAGE_ENDPOINT(), {
        productionOrderId: oid, sequenceNo: seqNo, sequence: { ...row, sequenceNo: seqNo },
        stageStartDate: ss.date, stageStartTime: ss.time, stageOutputQty: ss.outputQty ?? null,
        completedBy: String(user?.userId),
      });
      setSuccessMsg(`Stage ${seqNo} completed.`);
      toast.success(`Stage ${seqNo} completed successfully!`);
      await fetchExecutionRows(oid);
      console.log("Complete stage response:", res.data);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message ?? err.message ?? "Failed to complete stage");
    } finally { setLoading(false); }
  };

  /* verify */
  const handleVerify = async () => {
    try {
      const { data: stagesData } = await axios.get(`${Base_URL}/ProductionOrder/executeStages/id?id=${productionOrderId}`);
      const total = Array.isArray(stagesData) ? stagesData.length : 0;
      let done = 0;
      (stagesData || []).forEach((s) => { if (s.stageStatus === "Complete") done++; });
      setDeliveryStatus(total === done && total !== 0);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message ?? err.message ?? "Failed to verify stages");
    }
  };

  /* derive SFGs */
  const deriveSfgsForDelivery = (deliveryItemCode) => {
    const sfgSet = new Map();
    (executionRows || []).forEach((r) => {
      const code = r.orderStgOutputItem ?? r.planStgOutputItem ?? r.itemCodeId ?? r.outputItemCode ?? null;
      if (!code || (deliveryItemCode && String(code) === String(deliveryItemCode))) return;
      if (!sfgSet.has(code)) sfgSet.set(code, { itemCodeId: code, itemName: itemMap[code] ?? code, consumedQty: "" });
    });
    return Array.from(sfgSet.values());
  };

  /* open delivery modal */
  const handleDelivery = async () => {
    setDeliveryError(""); setDeliveryData([]); setItemMap({});
    setActualOutputs({}); setDeliverySfgMap({}); setDeliveryExtras({});
    const oid = queryOrderId || productionOrderId;
    if (!oid) { setDeliveryError("Missing production order id to fetch delivery data."); return; }
    try {
      setDeliveryLoading(true);
      const res = await axios.get(`${Base_URL}/ProductionOrder/orderOutput/id?id=${encodeURIComponent(oid)}`);
      const arr = Array.isArray(res?.data) ? res.data : [];
      setDeliveryData(arr);
      let map = {};
      try {
        const ir = await axios.get(`${Base_URL}/Item/ItemGetData`);
        (Array.isArray(ir?.data) ? ir.data : []).forEach((item) => {
          const key  = item.itemCodeId ?? item.itemCode ?? item.item_code ?? item.itemCodeID ?? item.code;
          const name = item.itemName ?? item.itemDesc ?? item.description ?? item.name ?? (item.outputItem && item.outputItem.itemName) ?? "";
          if (key) map[key] = name;
        });
        setItemMap(map);
      } catch (ie) { console.warn("Failed to fetch item names:", ie); }
      const sfgMap = {}, extrasInit = {};
      arr.forEach((d, idx) => {
        const ps = d.sfgItems ?? d.consumedItems ?? d.sfgConsumed ?? null;
        sfgMap[idx] = Array.isArray(ps) && ps.length > 0
          ? ps.map((s) => ({ itemCodeId: s.itemCodeId ?? s.itemCode ?? s.code ?? (s.outputItem && s.outputItem.itemCodeId) ?? "", itemName: s.itemName ?? s.itemDesc ?? s.name ?? (s.outputItem && s.outputItem.itemName) ?? (map[s.itemCodeId] ?? ""), consumedQty: s.consumedQty ?? s.qty ?? "" }))
          : deriveSfgsForDelivery(d.itemCodeId);
        extrasInit[idx] = { batchId: d.batchId ?? d.batch_id ?? "", mrpInclGst: d.mrpIncludingGst ?? d.mrpInclGst ?? "", ptrExclGst: d.ptrExcludingGst ?? d.ptrExclGst ?? "", mfgDate: d.mfgDate ?? "", expDate: d.expDate ?? "" };
      });
      setDeliverySfgMap(sfgMap); setDeliveryExtras(extrasInit);
      setShowDeliveryModal(true);
      console.log("Delivery data:", arr);
    } catch (err) {
      console.error(err);
      setDeliveryError(err.response?.data?.message ?? err.message ?? "Failed to fetch delivery data");
    } finally { setDeliveryLoading(false); }
  };

  const updateActualOutput    = (idx, v)          => setActualOutputs((p) => ({ ...p, [idx]: v }));
  const updateDeliverySfgQty  = (di, si, v)       => setDeliverySfgMap((p) => { const c = { ...p }; const a = [...(c[di] || [])]; a[si] = { ...a[si], consumedQty: v }; c[di] = a; return c; });
  const updateDeliveryExtra   = (idx, field, v)   => setDeliveryExtras((p) => ({ ...p, [idx]: { ...(p[idx] || {}), [field]: v } }));

  /* confirm delivery */
  const handleConfirmDelivery = async () => {
    const payload = (deliveryData || []).map((d, idx) => {
      const sfgConsumed = (deliverySfgMap[idx] || []).map((s) => ({ itemCodeId: s.itemCodeId, itemName: s.itemName ?? itemMap[s.itemCodeId] ?? "", consumedQty: s.consumedQty !== undefined && s.consumedQty !== "" ? Number(s.consumedQty) : 0 }));
      const ex = deliveryExtras[idx] || {};
      return { productionOrderId: d.productionOrderId, itemCodeId: d.itemCodeId, itemName: itemMap[d.itemCodeId] ?? d.itemName ?? null, productionOrderQty: Number(d.productionOrderQty ?? 0), productionOrderQtyUnit: d.productionOrderQtyUnit ?? "", actualOutputQty: actualOutputs[idx] !== undefined && actualOutputs[idx] !== "" ? Number(actualOutputs[idx]) : 0, createdBy: String(user?.userId ?? ""), organisationId: Number(user?.organisationId ?? 0), sfgConsumed, batchId: ex.batchId || null, mrpIncludingGst: ex.mrpInclGst !== "" ? Number(ex.mrpInclGst) : 0, ptrExcludingGst: ex.ptrExclGst !== "" ? Number(ex.ptrExclGst) : 0, mfgDate: ex.mfgDate || null, expDate: ex.expDate || null };
    });
    console.log("Confirm delivery payload (sent):", payload);
    setConfirmLoading(true);
    try {
      await axios.post(`${Base_URL}/ProductionOrder/deliverOrder`, { order: payload }, { headers: { "Content-Type": "application/json" } });
      toast.success("Order delivered successfully!");
      setSuccessMsg("Delivery confirmed successfully.");
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message ?? "Failed to deliver order");
    } finally { setConfirmLoading(false); }
  };

  const handleReset = () => {
    setStartDate(""); setStartTime(""); setError(""); setSuccessMsg("");
    setExecutionRows([]); setStageStarts({}); setQueryOrderId(productionOrderId ?? "");
  };

  /* ─── render ─── */
  return (
    <div style={{
      minHeight: "100vh", background: T.gray50, padding: "28px 24px 56px",
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    }}>

      {/* ── Page header ── */}
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: T.gray900, letterSpacing: "-0.3px" }}>
          Execute Production Order
        </h2>
        <p style={{ marginTop: 4, fontSize: 13, color: T.gray600 }}>
          Order:&nbsp;
          <span style={{ fontWeight: 600, color: T.indigo }}>{productionOrderId || "(enter below)"}</span>
        </p>
      </div>

      {/* Alerts */}
      <Banner type="danger"  msg={error}      onClose={() => setError("")} />
      <Banner type="success" msg={successMsg} onClose={() => setSuccessMsg("")} />

      {/* ── Card 1: Order setup ── */}
      <Card title="Order setup">

        {/* Row A — Order ID + action buttons */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 16, alignItems: "flex-end", marginBottom: 20 }}>
          <Field label="Production order ID">
            <Input
              value={queryOrderId}
              onChange={(e) => setQueryOrderId(e.target.value)}
              placeholder="e.g. PRO0000001"
            />
          </Field>

          {/* Inline button group — Load + Reset */}
          <div style={{ display: "flex", gap: 8 }}>
            <Btn variant="outline" onClick={() => fetchExecutionRows(queryOrderId)} disabled={loading || !queryOrderId}>
              {loading ? <Spinner animation="border" size="sm" /> : null}
              Load stages
            </Btn>
            <Btn variant="ghost" onClick={handleReset} disabled={loading}>
              Reset
            </Btn>
          </div>
        </div>

        {/* Row B — Date / Time / User (3-col grid) */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "14px 20px" }}>
          <Field label="Start date">
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </Field>
          <Field label="Start time">
            <Input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} />
          </Field>
          {/* <Field label="Logged-in user">
            <Input value={user?.userId ? String(user.userId) : "Not signed in"} readOnly />
          </Field> */}
        </div>

   

        {/* ── Primary action — Start Execution ── */}
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 18, paddingTop: 14, borderTop: `1px solid ${T.gray100}` }}>
          <Btn variant="primary" onClick={handleExecute} disabled={loading}>
            {loading
              ? <><Spinner animation="border" size="sm" />&nbsp;Executing…</>
              : "Start execution"}
          </Btn>
        </div>
      </Card>

      {/* ── Card 2: Execution steps (conditional) ── */}
      {executionRows.length > 0 && (
        <Card title={`Execution steps — ${executionRows.length} stage${executionRows.length !== 1 ? "s" : ""}`}>
          <div style={{ overflowX: "auto", borderRadius: T.radius, border: `1px solid ${T.gray200}` }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 1300 }}>
              <thead>
                <tr>
                  {["Step","Process","Subprocess","Station","Duration","SFG","Item name","SFG qty","Unit","Output qty","Stage date","Stage time","Action"].map((h, i) => (
                    <th key={i} style={{
                      background: T.indigo, color: T.white, fontWeight: 600, fontSize: 11,
                      letterSpacing: "0.5px", textTransform: "uppercase", padding: "11px 14px",
                      whiteSpace: "nowrap", textAlign: i === 12 ? "center" : "left",
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {executionRows.map((r, i) => {
                  const seqNo    = r.orderProductionProcessSeq ?? r.planProductionProcessSeq ?? i + 1;
                  const ss       = stageStarts[i] ?? { date: "", time: "", outputQty: "" };
                  const itemCode = r.orderStgOutputItem ?? r.planStgOutputItem ?? r.itemCodeId ?? r.outputItemCode ?? "";
                  const iName    = itemCode && itemMap[itemCode] ? itemMap[itemCode] : itemCode || "—";

                  return (
                    <tr key={`${r.planProductionOrderId || queryOrderId || productionOrderId}-${i}`}
                        style={{ background: i % 2 === 0 ? T.white : "#fafbff" }}>
                      <td style={{ padding: "10px 14px", borderBottom: `1px solid ${T.gray100}`, verticalAlign: "middle" }}>
                        <StepBadge n={seqNo} />
                      </td>
                      <td style={{ padding: "10px 14px", borderBottom: `1px solid ${T.gray100}`, verticalAlign: "middle", color: T.gray800 }}>{r.orderProductionProcessId   ?? r.planProductionProcessId   ?? "—"}</td>
                      <td style={{ padding: "10px 14px", borderBottom: `1px solid ${T.gray100}`, verticalAlign: "middle", color: T.gray800 }}>{r.orderProductionSubprocessId ?? r.planProductionSubprocessId ?? "—"}</td>
                      <td style={{ padding: "10px 14px", borderBottom: `1px solid ${T.gray100}`, verticalAlign: "middle", color: T.gray800 }}>{r.orderProductionStationId    ?? r.planProductionStationId    ?? "—"}</td>
                      <td style={{ padding: "10px 14px", borderBottom: `1px solid ${T.gray100}`, verticalAlign: "middle", color: T.gray800, whiteSpace: "nowrap" }}>
                        {(r.orderApproxDuration ?? r.planApproxDuration) ?? "—"}
                        {(r.orderApproxDurationUnit ?? r.planApproxDurationUnit) ? ` ${r.orderApproxDurationUnit ?? r.planApproxDurationUnit}` : ""}
                      </td>
                      <td style={{ padding: "10px 14px", borderBottom: `1px solid ${T.gray100}`, verticalAlign: "middle", color: T.gray800 }}>{r.orderStgOutputItem ?? r.planStgOutputItem ?? "—"}</td>
                      <td style={{ padding: "10px 14px", borderBottom: `1px solid ${T.gray100}`, verticalAlign: "middle", color: T.gray800, maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={iName}>{iName}</td>
                      <td style={{ padding: "10px 14px", borderBottom: `1px solid ${T.gray100}`, verticalAlign: "middle", color: T.gray800 }}>{(r.orderStgOutputQty  ?? r.planStgOutputQty)  ?? "—"}</td>
                      <td style={{ padding: "10px 14px", borderBottom: `1px solid ${T.gray100}`, verticalAlign: "middle", color: T.gray800 }}>{(r.orderStgOutputUnit ?? r.planStgOutputUnit) ?? "—"}</td>

                      {/* editable cells */}
                      <td style={{ padding: "8px 10px", borderBottom: `1px solid ${T.gray100}`, verticalAlign: "middle", minWidth: 130 }}>
                        <CellInput type="number" step="any" value={ss.outputQty ?? ""} onChange={(e) => updateStageStart(i, "outputQty", e.target.value)} placeholder="Qty" />
                      </td>
                      <td style={{ padding: "8px 10px", borderBottom: `1px solid ${T.gray100}`, verticalAlign: "middle", minWidth: 145 }}>
                        <CellInput type="date" value={ss.date} onChange={(e) => updateStageStart(i, "date", e.target.value)} />
                      </td>
                      <td style={{ padding: "8px 10px", borderBottom: `1px solid ${T.gray100}`, verticalAlign: "middle", minWidth: 120 }}>
                        <CellInput type="time" value={ss.time} onChange={(e) => updateStageStart(i, "time", e.target.value)} />
                      </td>
                      <td style={{ padding: "8px 14px", borderBottom: `1px solid ${T.gray100}`, verticalAlign: "middle", textAlign: "center" }}>
                        <Btn variant="success" size="sm" onClick={() => handleCompleteStage(r, i)} disabled={r.prdOrdseqActive === false}>
                          {loading ? <Spinner animation="border" size="sm" /> : "Complete"}
                        </Btn>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* ── Action strip: Verify + Deliver ── */}
      <div style={{
        display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
        background: T.white, borderRadius: T.radiusLg, border: `1px solid ${T.gray200}`,
        padding: "16px 20px", boxShadow: T.shadow,
      }}>
        <Btn variant="outline" onClick={handleVerify}>
          Verify stages
        </Btn>

        {/* visual divider */}
        <div style={{ width: 1, height: 22, background: T.gray200 }} />

        <Btn variant="primary" onClick={handleDelivery} disabled={!deliveryStatus || deliveryLoading}>
          {deliveryLoading
            ? <><Spinner animation="border" size="sm" />&nbsp;Loading…</>
            : "Deliver order"}
        </Btn>

        {!deliveryStatus && (
          <span style={{ fontSize: 12, color: T.gray400, marginLeft: 4 }}>
            Verify all stages to unlock delivery.
          </span>
        )}
      </div>


      {/* ═══════════════════════════════════════
          DELIVERY MODAL
      ═══════════════════════════════════════ */}
      <Modal
        show={showDeliveryModal}
        onHide={() => setShowDeliveryModal(false)}
        size="lg"
        centered
        contentClassName="border-0 overflow-hidden"
        style={{ fontFamily: "'Inter', sans-serif" }}
      >
        {/* ── Modal header ── */}
        <div style={{
          background: T.indigo, padding: "18px 24px", position: "relative",
          borderRadius: "12px 12px 0 0",
        }}>
          <div style={{ fontSize: 17, fontWeight: 700, color: T.white }}>
            Delivery / order output
          </div>
          <div style={{ fontSize: 12, color: "rgba(255,255,255,0.65)", marginTop: 2 }}>
            {queryOrderId || productionOrderId}
          </div>
          <button
            onClick={() => setShowDeliveryModal(false)}
            style={{
              position: "absolute", top: 14, right: 18,
              background: "rgba(255,255,255,0.15)", border: "none",
              borderRadius: "50%", width: 28, height: 28, color: T.white,
              fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
            }}
            aria-label="Close"
          >✕</button>
        </div>

        {/* ── Modal body ── */}
        <div style={{ background: T.gray50, padding: "20px 24px", maxHeight: "70vh", overflowY: "auto" }}>
          <Banner type="danger" msg={deliveryError} />

          {!deliveryError && deliveryLoading && (
            <div style={{ textAlign: "center", padding: "40px 0" }}>
              <Spinner animation="border" style={{ color: T.indigo }} />
            </div>
          )}

          {!deliveryLoading && Array.isArray(deliveryData) && deliveryData.length === 0 && (
            <div style={{ background: T.gray100, border: `1px dashed ${T.gray300}`, borderRadius: T.radius, padding: "18px 20px", fontSize: 13, color: T.gray400, textAlign: "center" }}>
              No delivery data returned from the API.
            </div>
          )}

          {!deliveryLoading && deliveryData.length > 0 &&
            deliveryData.map((d, idx) => {
              const sfgList  = deliverySfgMap[idx] ?? [];
              const iName    = itemMap[d.itemCodeId] ?? d.itemName ?? "—";
              const extra    = deliveryExtras[idx] || { batchId: "", mrpInclGst: "", ptrExclGst: "", mfgDate: "", expDate: "" };

              return (
                <DeliveryAccordionItem
                  key={`${d.productionOrderId}-${d.itemCodeId}-${idx}`}
                  idx={idx}
                  d={d}
                  iName={iName}
                  sfgList={sfgList}
                  extra={extra}
                  actualOutput={actualOutputs[idx] ?? ""}
                  itemMap={itemMap}
                  defaultOpen={deliveryData.length === 1}
                  onActualChange={(v) => updateActualOutput(idx, v)}
                  onSfgQtyChange={(si, v) => updateDeliverySfgQty(idx, si, v)}
                  onExtraChange={(field, v) => updateDeliveryExtra(idx, field, v)}
                />
              );
            })
          }
        </div>

        {/* ── Modal footer ── */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          background: T.white, borderTop: `1px solid ${T.gray200}`, padding: "14px 24px",
        }}>
          <span style={{ fontSize: 12, color: T.gray400 }}>
            {Array.isArray(deliveryData) ? deliveryData.length : 0} delivery item(s)
          </span>
          <div style={{ display: "flex", gap: 8 }}>
            <Btn variant="ghost" onClick={() => setShowDeliveryModal(false)}>Cancel</Btn>
            <Btn variant="primary" onClick={handleConfirmDelivery} disabled={deliveryLoading || confirmLoading}>
              {confirmLoading
                ? <><Spinner animation="border" size="sm" />&nbsp;Confirming…</>
                : "Confirm delivery"}
            </Btn>
          </div>
        </div>
      </Modal>

    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
   Delivery accordion item (extracted for cleanliness)
───────────────────────────────────────────────────────────────── */
function DeliveryAccordionItem({ idx, d, iName, sfgList, extra, actualOutput, itemMap, defaultOpen, onActualChange, onSfgQtyChange, onExtraChange }) {
  const [open, setOpen] = useState(defaultOpen);

  const T2 = {
    indigo: "#4f46e5", indigoLight: "#eef2ff", gray50: "#f5f6fa", gray100: "#f0f1f5",
    gray200: "#e9eaf0", gray300: "#d1d5db", gray400: "#9ca3af", gray600: "#6b7280",
    gray800: "#374151", gray900: "#111827", white: "#ffffff", blue50: "#f0f9ff", blue700: "#0369a1",
  };

  return (
    <div style={{ background: T2.white, borderRadius: 14, border: `1px solid ${T2.gray200}`, marginBottom: 12, overflow: "hidden" }}>

      {/* header */}
      <div
        onClick={() => setOpen((v) => !v)}
        style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "13px 16px", cursor: "pointer", userSelect: "none" }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <StepBadge n={idx + 1} />
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: T2.gray900 }}>{d.itemCodeId ?? "—"}</div>
            <div style={{ fontSize: 12, color: T2.gray600, marginTop: 1, maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{iName}</div>
          </div>
          <div style={{ fontSize: 12, color: T2.gray600, display: "flex", alignItems: "center", gap: 4, marginLeft: 4 }}>
            Order qty:
            <span style={{ background: T2.blue50, color: T2.blue700, padding: "2px 8px", borderRadius: 6, fontSize: 11, fontWeight: 600 }}>
              {d.productionOrderQty ?? "—"}{d.productionOrderQtyUnit ? ` ${d.productionOrderQtyUnit}` : ""}
            </span>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <input
            type="number" step="any" placeholder="Actual qty"
            value={actualOutput}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => onActualChange(e.target.value)}
            style={{ width: 120, height: 34, borderRadius: 8, border: `1px solid ${T2.gray200}`, padding: "0 10px", fontSize: 13, color: T2.gray900, background: T2.white, outline: "none", fontFamily: "inherit" }}
          />
          <span style={{ fontSize: 14, color: T2.gray400, transform: open ? "rotate(180deg)" : "none", transition: "transform 0.2s", display: "block" }}>▾</span>
        </div>
      </div>

      {/* body */}
      {open && (
        <div style={{ padding: "0 16px 18px", borderTop: `1px solid ${T2.gray100}` }}>

          {/* meta row */}
          <div style={{ display: "flex", gap: 28, padding: "14px 0 6px", fontSize: 13 }}>
            {[["Production order", d.productionOrderId ?? "—"], ["Item code", d.itemCodeId ?? "—"], ["Item name", iName]].map(([lbl, val]) => (
              <div key={lbl}>
                <div style={{ color: T2.gray400, fontSize: 11, marginBottom: 3 }}>{lbl}</div>
                <div style={{ fontWeight: lbl === "Item name" ? 400 : 600, color: lbl === "Item name" ? T2.gray600 : T2.gray900 }}>{val}</div>
              </div>
            ))}
          </div>

          {/* batch & pricing */}
          <SectionLabel>Batch &amp; pricing details</SectionLabel>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "12px 16px" }}>
            {[
              { label: "Batch ID",      field: "batchId",    type: "text",   ph: "e.g. B-2024-001" },
              { label: "MRP incl. GST", field: "mrpInclGst", type: "number", ph: "0.00" },
              { label: "PTR excl. GST", field: "ptrExclGst", type: "number", ph: "0.00" },
            ].map(({ label, field, type, ph }) => (
              <div key={field}>
                <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: T2.gray800, marginBottom: 5 }}>{label}</label>
                <input type={type} step={type === "number" ? "any" : undefined} value={extra[field]} onChange={(e) => onExtraChange(field, e.target.value)} placeholder={ph}
                  style={{ width: "100%", height: 38, borderRadius: 10, border: `1px solid ${T2.gray200}`, padding: "0 11px", fontSize: 13, color: T2.gray900, background: T2.white, outline: "none", boxSizing: "border-box", fontFamily: "inherit" }} />
              </div>
            ))}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "12px 16px", marginTop: 12 }}>
            {[
              { label: "Mfg date", field: "mfgDate", type: "date" },
              { label: "Exp date", field: "expDate", type: "date" },
            ].map(({ label, field, type }) => (
              <div key={field}>
                <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: T2.gray800, marginBottom: 5 }}>{label}</label>
                <input type={type} value={extra[field]} onChange={(e) => onExtraChange(field, e.target.value)}
                  style={{ width: "100%", height: 38, borderRadius: 10, border: `1px solid ${T2.gray200}`, padding: "0 11px", fontSize: 13, color: T2.gray900, background: T2.white, outline: "none", boxSizing: "border-box", fontFamily: "inherit" }} />
              </div>
            ))}
          </div>

          {/* SFG table */}
          <SectionLabel>SFG items consumed</SectionLabel>

          {sfgList.length === 0 ? (
            <div style={{ background: T2.gray50, border: `1px dashed ${T2.gray300}`, borderRadius: 10, padding: "14px 16px", fontSize: 13, color: T2.gray400, textAlign: "center" }}>
              No SFG items detected for this delivery item.
            </div>
          ) : (
            <div style={{ overflowX: "auto", borderRadius: 10, border: `1px solid ${T2.gray200}` }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr>
                    {["#","Item code","Item name","Consumed qty"].map((h, i) => (
                      <th key={i} style={{ background: T2.indigoLight, color: T2.indigo, fontWeight: 700, fontSize: 11, padding: "9px 12px", textAlign: i === 0 ? "center" : "left", letterSpacing: "0.4px", textTransform: "uppercase" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sfgList.map((s, sIdx) => (
                    <tr key={`sfg-${s.itemCodeId}-${sIdx}`} style={{ background: sIdx % 2 === 0 ? T2.white : T2.gray50 }}>
                      <td style={{ padding: "9px 12px", borderTop: `1px solid ${T2.gray100}`, textAlign: "center", verticalAlign: "middle" }}>
                        <StepBadge n={sIdx + 1} size={22} />
                      </td>
                      <td style={{ padding: "9px 12px", borderTop: `1px solid ${T2.gray100}`, fontWeight: 600, color: T2.gray900, verticalAlign: "middle" }}>{s.itemCodeId ?? "—"}</td>
                      <td style={{ padding: "9px 12px", borderTop: `1px solid ${T2.gray100}`, color: T2.gray600, verticalAlign: "middle" }}>{s.itemName ?? itemMap[s.itemCodeId] ?? "—"}</td>
                      <td style={{ padding: "8px 12px", borderTop: `1px solid ${T2.gray100}`, verticalAlign: "middle" }}>
                        <input type="number" step="any" value={s.consumedQty ?? ""} onChange={(e) => onSfgQtyChange(sIdx, e.target.value)} placeholder="0"
                          style={{ maxWidth: 160, height: 34, borderRadius: 8, border: `1px solid ${T2.gray200}`, padding: "0 10px", fontSize: 13, color: T2.gray900, background: T2.white, outline: "none", fontFamily: "inherit" }} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <p style={{ fontSize: 12, color: T2.gray400, textAlign: "right", marginTop: 10, marginBottom: 0 }}>
            Verify quantities and details before confirming delivery.
          </p>
        </div>
      )}
    </div>
  );
}